import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { LocalStorageService } from '../../infrastructure/storage/local-storage.service';
import { STORAGE_KEYS } from '../../infrastructure/storage/storage-keys';
import { AuthService } from '../services/auth.service';

/** Endpoints que NO llevan Bearer (login y refresh son públicos). */
const SKIP_BEARER = ['/auth/login', '/auth/refresh'];

/**
 * Interceptor de autenticación:
 *  - Adjunta `Authorization: Bearer <access_token>` a las peticiones protegidas.
 *  - Ante un 401 (no en `/auth/*`), renueva los tokens una vez (single-flight)
 *    y reintenta la petición original. Si el refresh falla, fuerza el logout.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(LocalStorageService);
  const auth = inject(AuthService);

  const skipBearer = SKIP_BEARER.some((path) => req.url.includes(path));
  const token = storage.get<string>(STORAGE_KEYS.accessToken);

  const authReq =
    token && !skipBearer
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      const isAuthEndpoint = req.url.includes('/auth/');
      if (err.status !== 401 || isAuthEndpoint) {
        return throwError(() => err);
      }

      // Access token expirado → un solo refresh compartido, luego reintento.
      return auth.refreshTokens().pipe(
        catchError((refreshErr) => {
          auth.forceLogout();
          return throwError(() => refreshErr);
        }),
        switchMap((newToken) =>
          next(authReq.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } })),
        ),
      );
    }),
  );
};
